import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Link
} from '@mui/material';
import { Error as ErrorIcon, SyncProblem as SyncProblemIcon } from '@mui/icons-material';
import { buildCrmApiUrl } from '../../api/apiConfig';

const issueTypeConfig = {
  recurring_expired: { label: 'Recurring Expired', color: 'error', icon: ErrorIcon },
  recurring_installment_failing: { label: 'Installment Failing', color: 'warning', icon: SyncProblemIcon }
};

const RecurringIssuesDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [filterTab, setFilterTab] = useState(2); // 0=all, 1=expired, 2=failing (default)
  const [includeExpired, setIncludeExpired] = useState(false);

  const fetchRecurringIssues = async () => {
    setLoading(true);
    setError(null);
    try {
      const number = localStorage.getItem('number') || '';
      const params = new URLSearchParams();
      if (includeExpired) params.set('include_expired', 'true');
      if (number) params.set('number', number);
      const qs = params.toString();
      const res = await fetch(
        `${buildCrmApiUrl('clients/recurring-issues')}${qs ? '?' + qs : ''}`,
        { headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' } }
      );
      const data = await res.json();
      if (data.success) {
        setUsers(data.data || []);
      } else {
        setError(data.error || 'Failed to load');
      }
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecurringIssues();
  }, [includeExpired]);

  const handleNumberClick = (number) => {
    window.open(`/sales/profile/${number}`, '_blank', 'noopener,noreferrer');
  };

  const filteredUsers = users.filter((u) => {
    if (filterTab === 1) return u.issue_type === 'recurring_expired';
    if (filterTab === 2) return u.issue_type === 'recurring_installment_failing';
    return true;
  });

  const expiredCount = users.filter((u) => u.issue_type === 'recurring_expired').length;
  const failingCount = users.filter((u) => u.issue_type === 'recurring_installment_failing').length;

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Recurring Issues
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Installment failing (default). Both expired and failing show last 7 days only.
        </Typography>
      </Box>

      {/* Include expired toggle */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Chip
          label={includeExpired ? 'Including Expired' : 'Installment Failing Only'}
          color={includeExpired ? 'primary' : 'default'}
          size="small"
          onClick={() => setIncludeExpired(!includeExpired)}
          variant="outlined"
        />
      </Box>

      {/* Summary chips */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Chip
          label={`Expired: ${expiredCount}`}
          color="error"
          size="small"
          onClick={() => setFilterTab(1)}
          variant={filterTab === 1 ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Installment Failing: ${failingCount}`}
          color="warning"
          size="small"
          onClick={() => setFilterTab(2)}
          variant={filterTab === 2 ? 'filled' : 'outlined'}
        />
        <Chip
          label={`All: ${users.length}`}
          color="default"
          size="small"
          onClick={() => setFilterTab(0)}
          variant={filterTab === 0 ? 'filled' : 'outlined'}
        />
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Number</strong></TableCell>
                <TableCell><strong>Issue Type</strong></TableCell>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Expiry / Last Failed</strong></TableCell>
                <TableCell><strong>Failed Attempts</strong></TableCell>
                <TableCell><strong>Owner</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((u, idx) => {
                const config = issueTypeConfig[u.issue_type] || { label: u.issue_type, color: 'default' };
                const dateStr = u.issue_type === 'recurring_expired'
                  ? (u.recurring_expiry ? new Date(u.recurring_expiry).toLocaleDateString() : '-')
                  : (u.last_failed_at ? new Date(u.last_failed_at).toLocaleString() : '-');
                return (
                  <TableRow key={`${u.number}-${u.issue_type}-${u.token_id || '-'}-${idx}`} hover>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleNumberClick(u.number)}
                        sx={{ cursor: 'pointer', textAlign: 'left' }}
                      >
                        {u.number}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Chip label={config.label} color={config.color} size="small" />
                    </TableCell>
                    <TableCell>{u.plan_name || '-'}</TableCell>
                    <TableCell>{dateStr}</TableCell>
                    <TableCell>{u.failed_attempt_count > 0 ? u.failed_attempt_count : '-'}</TableCell>
                    <TableCell>{u.owner || '-'}</TableCell>
                    <TableCell>{u.email || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredUsers.length === 0 && !loading && (
          <Box p={3} textAlign="center">
            <Typography color="text.secondary">No recurring issues found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RecurringIssuesDashboard;
